import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { switchMap, tap } from 'rxjs/operators';

import { ApWindowService } from '@ap/cdk';
import { ApContentService, ContentPrefix } from '@ap/content';
import { ContractDetails } from '@ap/interface-apix-mijn-overzicht';
import { ApModalService, ModalContent, ModalOptions } from '@ap/modal';

import { MijnTemplateAnalyticsService } from '../../analytics/analytics.service';
import content from '../../mijn-template.content.json';
import { MijnTemplateService, XC_HEADLESS_CONTENT } from '../../mijn-template.service';

import { ACTIE_TEMPLATE_HELP_BUTTON, HELP_CONTEXT_ITEM, HELP_CONTEXT_ITEM_LINK } from './context-help-modal.const';
import { ContextHelpModalOptions, HelpContextItem } from './context-help-modal.model';

/**
 * @module ContextHelpModalModule
 * @ngdoc component
 * @name ContextHelpModalComponent
 */
@Component({
    selector: 'context-help-modal',
    templateUrl: 'context-help-modal.component.html',
    standalone: false
})
export class ContextHelpModalComponent extends ModalContent<ContextHelpModalOptions> implements OnInit {
    public reload: boolean;
    public helpItems: HelpContextItem[] = [];
    public contractDetails: ContractDetails;

    public constructor(
        private readonly changeDetector: ChangeDetectorRef,
        private readonly contentService: ApContentService,
        private readonly analyticsService: MijnTemplateAnalyticsService,
        private readonly mijnTemplateService: MijnTemplateService,
        private readonly modalService: ApModalService,
        private readonly windowService: ApWindowService
    ) {
        super();
    }

    public ngOnInit(): void {
        if (this.modalOptions.options.context) {
            // when context is filled the user clicked on a help item
            this.analyticsService.clickEvent(
                HELP_CONTEXT_ITEM,
                `${this.modalOptions.options.helpContext}-${this.modalOptions.options.context.title}`
            );
        } else {
            this.contentService.addContentItems(content);

            this.mijnTemplateService
                .getContractDetails()
                .pipe(
                    tap((contractDetails) => (this.contractDetails = contractDetails)),
                    switchMap((contractDetails) =>
                        this.mijnTemplateService.getHeadlessContent(XC_HEADLESS_CONTENT, contractDetails.is_zakelijk)
                    )
                )
                .subscribe((headlessContent) => {
                    if (headlessContent) {
                        this.reload = true;
                        this.contentService.removeContentItems(content);
                        this.contentService.addHeadlessContentItems(headlessContent);
                        this.changeDetector.detectChanges();
                        this.reload = false;
                    }
                    if (this.contractDetails.is_zakelijk) {
                        this.contentService.setContentPrefix(ContentPrefix.BUSINESS);
                    }

                    this.loadHelpItems(this.modalOptions.options.helpContext);
                });
            this.analyticsService.clickEvent(ACTIE_TEMPLATE_HELP_BUTTON, this.modalOptions.options.helpContext);
        }
    }

    public loadHelpItems(context: string) {
        const headlessContextContent = `@ap/mijn-template-${context}-contextHelp-content`;
        this.mijnTemplateService
            .getHeadlessContent(headlessContextContent, this.contractDetails.is_zakelijk)
            .subscribe((headlessContent) => {
                if (headlessContent) {
                    let index = 1;
                    while (headlessContent[`mijn.actie.template.contextHelp.${context}.question.${index}`]) {
                        this.helpItems.push({
                            name: `mijn.actie.template.contextHelp.${context}`,
                            title: headlessContent[`mijn.actie.template.contextHelp.${context}.question.${index}`],
                            subTitle: headlessContent[`mijn.actie.template.contextHelp.${context}.answer.${index}`],
                            linkText: headlessContent[`mijn.actie.template.contextHelp.${context}.linkText.${index}`],
                            link: headlessContent[`mijn.actie.template.contextHelp.${context}.link.${index}`],
                            score: index++
                        });
                    }
                }
            });
    }

    public openDetailHelpContext(item: HelpContextItem): void {
        const modalOptions = new ModalOptions<ContextHelpModalOptions>({
            helpContext: this.modalOptions.options.helpContext,
            context: item
        });
        modalOptions.isSubModal = true;
        modalOptions.title = this.modalOptions.title;
        this.modalService.openSubModal(() => this.closeDetailHelpContext(), ContextHelpModalComponent, modalOptions);
    }

    public detailClick(item: HelpContextItem): void {
        this.analyticsService.clickEvent(HELP_CONTEXT_ITEM_LINK, `${this.modalOptions.options.helpContext}-${item.linkText}`);
        this.windowService.location_href(item.link);
    }

    public onOpenChat(): void {
        this.cancel();
        this.modalOptions.options.openChat?.();
    }

    public closeDetailHelpContext() {
        this.modalOptions.options.context = null;
        this.cancel();
    }

    /*  removeHtmlTags
     *  remove html tags from text, replace with space so content will not be glued together,
     *  make sure only a single space is present when sequential HTML tags are removed.
     */
    private removeHtmlTags(text: string) {
        return text
            ? text
                  .replace(/<[^>]*>/g, ' ')
                  .replace(/\s{2,}/g, ' ')
                  .trim()
            : undefined;
    }
}
